import i18n from './i18n';

const BASE = '/api';
const DETAIL_TRANSLATIONS = {
  'Invalid credentials': 'errors.invalidCredentials',
  'Account is banned': 'errors.accountBanned',
  'Current password is incorrect': 'errors.currentPasswordIncorrect',
  'Username already taken': 'errors.usernameTaken',
  'Email already registered': 'errors.emailRegistered',
  'Email already in use': 'errors.emailInUse',
  'Access denied': 'errors.accessDenied',
  'Invalid timezone': 'errors.invalidTimezone',
};

function getToken() {
  return localStorage.getItem('token');
}

async function request(method, path, body) {
  const headers = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let res;
  try {
    res = await fetch(BASE + path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new Error(i18n.t('errors.serviceUnavailable'));
  }

  if (res.status === 204) return null;
  if (res.status === 401 && token) {
    localStorage.removeItem('token');
    window.location.href = '/login';
    return null;
  }

  const contentType = res.headers?.get?.('content-type') || '';
  let data;
  if (contentType.toLowerCase().includes('application/json')) {
    try {
      data = await res.json();
    } catch {
      throw new Error(i18n.t('errors.serviceUnavailable'));
    }
  } else {
    // Reverse proxies commonly return an HTML error page while the API is
    // starting or unavailable. Never expose that markup (or JSON parse errors)
    // to the user.
    try { await res.text(); } catch { /* response body is intentionally ignored */ }
    throw new Error(i18n.t('errors.serviceUnavailable'));
  }

  if (!res.ok) {
    const detail = typeof data?.detail === 'string' ? data.detail : null;
    const translationKey = detail ? DETAIL_TRANSLATIONS[detail] : null;
    const message = translationKey ? i18n.t(translationKey) : detail;
    const err = new Error(message || i18n.t('errors.requestFailed'));
    err.status = res.status;
    throw err;
  }
  return data;
}

export const api = {
  login:             (username, password) => request('POST', '/auth/login', { username, password }),
  register:          (username, email, password) => request('POST', '/auth/register', { username, email, password }),
  me:                () => request('GET', '/auth/me'),
  updateMe:          (data)               => request('PATCH', '/auth/me', data),
  getTimezones:      ()                   => request('GET', '/auth/timezones'),
  updateTimezone:    (timezone)           => request('PATCH', '/auth/me/timezone', { timezone }),
  getStats:          ()                   => request('GET',  '/devices/stats'),
  getDeviceServer:   ()                   => request('GET',  '/device-link/server'),
  getDevices:        ()                   => request('GET',  '/devices'),
  getDevice:         (id)                 => request('GET',  `/devices/${id}`),
  createDeviceUiSession: (id)             => request('POST', `/devices/${id}/ui-session`),
  createDevice:      (data)               => request('POST', '/devices', data),
  updateDevice:      (id, data)           => request('PATCH', `/devices/${id}`, data),
  deleteDevice:      (id)                 => request('DELETE', `/devices/${id}`),
  getDeviceEvents:   (id, limit = 50)     => request('GET',  `/devices/${id}/events?limit=${limit}`),
  sendCommand:       (id, command, payload) => request('POST', `/devices/${id}/command`, { command, payload }),

  // Admin: provision tokens
  getTokens:         ()           => request('GET',    '/admin/provision-tokens'),
  createToken:       (data)       => request('POST',   '/admin/provision-tokens', data),
  revokeToken:       (id)         => request('DELETE', `/admin/provision-tokens/${id}`),

  // Max messenger auth
  maxLoginInit:      ()           => request('POST',   '/auth/max/login-init'),
  maxPoll:           (state)      => request('GET',    `/auth/max/poll/${state}`),

  // Public settings (no auth)
  getPublicSettings: ()           => request('GET',    '/settings/public'),

  // Admin: site settings
  getSettings:       ()           => request('GET',    '/admin/settings'),
  updateSetting:     (key, value) => request('PUT',    `/admin/settings/${key}`, { value }),

  // Admin: user management
  getUsers:          ()           => request('GET',    '/admin/users'),
  getUser:           (id)         => request('GET',    `/admin/users/${id}`),
  getUserDevices:    (id)         => request('GET',    `/admin/users/${id}/devices`),
  updateUser:        (id, data)   => request('PATCH',  `/admin/users/${id}`, data),
  deleteUser:        (id)         => request('DELETE', `/admin/users/${id}`),
  resetUserPassword: (id)         => request('POST',   `/admin/users/${id}/reset-password`),
};
